import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft } from 'lucide-react';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import primelaneLogo from '@/assets/primelane-logo.png';
import { CountryCodeSelect, getDefaultCountryCode } from '@/components/CountryCodeSelect';

const emailSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
});

const otpSchema = z.object({
    otp: z.string().length(6, 'OTP must be 6 digits'),
});

const phoneSchema = z.object({
    phone: z.string().min(5, 'Please enter a valid phone number').regex(/^\d+$/, 'Phone number must contain only digits'),
});

type AuthMethod = 'email' | 'phone';
type Step = 'input' | 'otp';

export default function Auth() {
    const [authMethod, setAuthMethod] = useState<AuthMethod>('phone');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState(getDefaultCountryCode());
    const [dialCode, setDialCode] = useState('+233');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState<Step>('input');
    const [isLoading, setIsLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const { sendOtp, verifyOtp, sendPhoneOtp, verifyPhoneOtp, signOut, user, isAuthorized, authResolved, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();

    // Single combined effect: only acts after checkAuthorization has definitively resolved.
    useEffect(() => {
        if (authLoading || !user) return;
        if (!authResolved) return;

        if (isAuthorized) {
            navigate('/summary');
        } else {
            signOut();
            toast({
                variant: 'destructive',
                title: 'Access Restricted',
                description: 'This app is only available for drivers. Please contact your fleet manager.',
                duration: 8000,
            });
        }
    }, [user, isAuthorized, authResolved, authLoading, navigate, signOut, toast]);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Auto-strip country code digits from phone input if user entered them
    const getCleanPhone = () => {
        const dialDigits = dialCode.replace('+', '');
        let p = phone;
        // If user entered the country code digits at the start, strip them
        if (p.startsWith(dialDigits)) {
            p = p.slice(dialDigits.length);
        }
        // Strip leading zero (local format → international format)
        if (p.startsWith('0')) {
            p = p.slice(1);
        }
        return `${dialCode}${p}`;
    };

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        if (authMethod === 'email') {
            const validation = emailSchema.safeParse({ email });
            if (!validation.success) {
                toast({ variant: 'destructive', title: 'Validation Error', description: validation.error.errors[0].message });
                return;
            }

            setIsLoading(true);
            const { error } = await sendOtp(email);

            if (error) {
                toast({ variant: 'destructive', title: 'Failed to send OTP', description: error.message });
                setIsLoading(false);
                return;
            }

            toast({ title: 'OTP Sent', description: 'Check your email for the verification code.' });
        } else {
            const validation = phoneSchema.safeParse({ phone });
            if (!validation.success) {
                toast({ variant: 'destructive', title: 'Validation Error', description: validation.error.errors[0].message });
                return;
            }

            setIsLoading(true);
            const { error } = await sendPhoneOtp(getCleanPhone());

            if (error) {
                toast({ variant: 'destructive', title: 'Failed to send OTP', description: error.message });
                setIsLoading(false);
                return;
            }

            toast({ title: 'OTP Sent', description: 'Check your phone for the verification code.' });
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setStep('otp');
            setIsTransitioning(false);
        }, 200);

        setResendCooldown(60);
        setIsLoading(false);
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = otpSchema.safeParse({ otp });
        if (!validation.success) {
            toast({ variant: 'destructive', title: 'Validation Error', description: validation.error.errors[0].message });
            return;
        }

        setIsLoading(true);

        const { error } = authMethod === 'email'
            ? await verifyOtp(email, otp)
            : await verifyPhoneOtp(getCleanPhone(), otp);

        if (error) {
            toast({ variant: 'destructive', title: 'Verification Failed', description: error.message });
            setIsLoading(false);
            return;
        }

        setIsLoading(false);
    };

    const handleResendOtp = async () => {
        if (resendCooldown > 0) return;

        setIsLoading(true);
        const { error } = authMethod === 'email'
            ? await sendOtp(email)
            : await sendPhoneOtp(getCleanPhone());

        if (error) {
            toast({ variant: 'destructive', title: 'Failed to resend OTP', description: error.message });
        } else {
            toast({
                title: 'OTP Resent',
                description: authMethod === 'email'
                    ? 'Check your email for the new verification code.'
                    : 'Check your phone for the new verification code.',
            });
            setResendCooldown(60);
        }
        setIsLoading(false);
    };

    const handleBack = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setStep('input');
            setOtp('');
            setIsTransitioning(false);
        }, 200);
    };

    const handleMethodChange = (method: AuthMethod) => {
        if (method === authMethod) return;
        setAuthMethod(method);
        setStep('input');
        setOtp('');
    };


    if (authLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 flex items-center justify-center px-4 py-12">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 w-full max-w-md p-8">
                <div
                    className={cn(
                        "transition-all duration-200 ease-out",
                        isTransitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
                    )}
                >
                    {step === 'input' ? (
                        <div className="space-y-8">
                            {/* Branding */}
                            <div className="text-center">
                                <img
                                    src={primelaneLogo}
                                    alt="Primelane"
                                    className="h-8 mx-auto mb-6"
                                />
                                <p className="text-base text-slate-500">
                                    Sign in to your account
                                </p>
                            </div>

                            {/* Method tabs */}
                            <div className="flex border-b border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => handleMethodChange('email')}
                                    className={cn(
                                        "flex-1 pb-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-px",
                                        authMethod === 'email'
                                            ? "border-slate-900 text-slate-900"
                                            : "border-transparent text-slate-400 hover:text-slate-600"
                                    )}
                                >
                                    Email
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleMethodChange('phone')}
                                    className={cn(
                                        "flex-1 pb-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-px",
                                        authMethod === 'phone'
                                            ? "border-slate-900 text-slate-900"
                                            : "border-transparent text-slate-400 hover:text-slate-600"
                                    )}
                                >
                                    Phone number
                                </button>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSendOtp} className="space-y-6">
                                {authMethod === 'email' ? (
                                    <div className="space-y-2">
                                        <label htmlFor="email" className="text-sm font-medium text-slate-700">
                                            Email address
                                        </label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder=""
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            autoComplete="email"
                                            className="h-12 px-4 text-base border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 focus:ring-slate-900/10 transition-all duration-200 rounded-xl"
                                        />
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <label htmlFor="phone" className="text-sm font-medium text-slate-700">
                                            Phone number
                                        </label>
                                        <div className="flex gap-2">
                                            <CountryCodeSelect
                                                value={countryCode}
                                                onChange={(code, dial) => {
                                                    setCountryCode(code);
                                                    setDialCode(dial);
                                                }}
                                            />
                                            <Input
                                                id="phone"
                                                type="tel"
                                                placeholder=""
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                                                required
                                                autoComplete="tel"
                                                className="h-12 px-4 text-base border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 focus:ring-slate-900/10 transition-all duration-200 rounded-xl flex-1"
                                            />
                                        </div>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl text-base transition-all duration-200 shadow-lg shadow-slate-900/20"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Sending code...
                                        </>
                                    ) : (
                                        'Sign in'
                                    )}
                                </Button>
                            </form>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Branding */}
                            <div className="text-center">
                                <img
                                    src={primelaneLogo}
                                    alt="Primelane"
                                    className="h-8 mx-auto mb-6"
                                />
                                <h1 className="text-2xl font-semibold text-slate-900 tracking-tight mb-2">
                                    {authMethod === 'email' ? 'Check your email' : 'Check your phone'}
                                </h1>
                                <p className="text-sm text-slate-500">
                                    We sent a 6-digit code to{' '}
                                    <span className="font-medium text-slate-900">
                                        {authMethod === 'email' ? email : getCleanPhone()}
                                    </span>
                                </p>
                            </div>



                            {/* Form */}
                            <form onSubmit={handleVerifyOtp} className="space-y-6">
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-slate-700">
                                        Verification code
                                    </label>
                                    <div className="flex justify-center">
                                        <InputOTP
                                            maxLength={6}
                                            value={otp}
                                            onChange={(value) => setOtp(value)}
                                        >
                                            <InputOTPGroup className="gap-2">
                                                {[0, 1, 2, 3, 4, 5].map((index) => (
                                                    <InputOTPSlot
                                                        key={index}
                                                        index={index}
                                                        className="h-12 w-10 text-xl font-semibold rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white focus:border-slate-900 focus:ring-slate-900/10 transition-all duration-200"
                                                    />
                                                ))}
                                            </InputOTPGroup>
                                        </InputOTP>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl text-base transition-all duration-200 shadow-lg shadow-slate-900/20"
                                    disabled={isLoading || otp.length !== 6}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Verifying...
                                        </>
                                    ) : (
                                        'Verify & Sign in'
                                    )}
                                </Button>

                                {/* Actions */}
                                <div className="flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="text-sm text-slate-500 hover:text-slate-900 font-medium flex items-center gap-1.5 transition-colors"
                                    >
                                        <ArrowLeft className="h-4 w-4" />
                                        Back
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={resendCooldown > 0 || isLoading}
                                        className="text-sm text-slate-500 hover:text-slate-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
